import * as stylex from '@stylexjs/stylex';
import {
  CatchBoundary,
  createFileRoute,
  Outlet,
  useCanGoBack,
  useMatches,
  useNavigate,
  useRouter,
} from '@tanstack/react-router';
import { Fragment, type ReactElement } from 'react';

import { ErrorState } from '@/components/error-state.component';
import { PendingState } from '@/components/pending-state.component';
import {
  ArrowLeftStrokeIcon,
  DETAILS_TOOLBAR_SLOTS,
  DetailsToolbar,
  Heading,
  IconButton,
  Text,
  View,
} from '@/libs/serenity-ui';
import { breakpoints } from '@/libs/serenity-ui/constants.stylex';
import { $ } from '@/libs/serenity-ui/tokens.stylex';

const scrollBorderAnimation = stylex.keyframes({
  from: { borderBottomColor: 'transparent' },
  to: { borderBottomColor: $.border06 },
});

export const Route = createFileRoute('/_main/_form')({
  component: RouteComponent,
  pendingComponent: PendingState,
});

function RouteComponent(): ReactElement {
  const router = useRouter();
  const matches = useMatches();
  const canGoBack = useCanGoBack();
  const navigate = useNavigate();

  const childMatch = matches.at(-1);
  const staticData = childMatch?.staticData?.form;

  const isHighlighted = router.history.location.hash === '#details';
  const isCentered = staticData?.toolbarLayout === 'centered';

  const Provider = staticData?.slots?.providerComponent ?? Fragment;

  const handleGoBack = staticData?.goBackFallbackPath ? () => {
    if (canGoBack) {
      router.history.back();
    } else {
      navigate({ to: staticData.goBackFallbackPath! });
    }
  } : undefined;

  return (
    <Provider>
      <View style={[ isCentered ? centeredStyles.base : fullWidthStyles.base, isHighlighted && fullWidthStyles.highlighted ]}>
        {isCentered ?
          <View style={centeredToolbarStyles.base}>
            {handleGoBack &&
              <IconButton
                aria-label="Go back"
                size={32}
                style={centeredToolbarStyles.goBackButton}
                variant="ghost"
                onPress={handleGoBack}>
                <ArrowLeftStrokeIcon />
              </IconButton>}
            <CatchBoundary errorComponent={() => null} getResetKey={() => 'reset'} onCatch={(error, errorInfo) => console.error(error, errorInfo)}>
              <Heading level={1} size={24} style={centeredToolbarStyles.heading}>
                {typeof staticData?.heading === 'function' ? <staticData.heading /> : staticData?.heading}
              </Heading>
            </CatchBoundary>
            {(staticData?.slots?.statusComponent || staticData?.slots?.actionsComponent) &&
              <View style={centeredToolbarStyles.actions}>
                <CatchBoundary errorComponent={() => null} getResetKey={() => 'reset'} onCatch={(error, errorInfo) => console.error(error, errorInfo)}>
                  {staticData?.slots?.statusComponent && <staticData.slots.statusComponent />}
                  {staticData?.slots?.actionsComponent && <staticData.slots.actionsComponent />}
                </CatchBoundary>
              </View>}
          </View> :
          <DetailsToolbar>
            {staticData?.slots?.topBarComponent &&
            <View slot={DETAILS_TOOLBAR_SLOTS.COLLAPSIBLE_ROW_VIEW}>
              <CatchBoundary errorComponent={() => null} getResetKey={() => 'reset'} onCatch={(error, errorInfo) => console.error(error, errorInfo)}>
                <staticData.slots.topBarComponent />
              </CatchBoundary>
            </View>}

            <View slot={DETAILS_TOOLBAR_SLOTS.ROW_VIEW}>
              <View slot={DETAILS_TOOLBAR_SLOTS.PAGE_HEADER_VIEW}>
                <CatchBoundary errorComponent={() => null} getResetKey={() => 'reset'} onCatch={(error, errorInfo) => console.error(error, errorInfo)}>
                  <Heading>{typeof staticData?.heading === 'function' ? <staticData.heading /> : staticData?.heading}</Heading>

                  <View slot={DETAILS_TOOLBAR_SLOTS.PAGE_SUBTITLE_VIEW}>
                    {staticData?.slots?.idComponent && <staticData.slots.idComponent />}
                    {staticData?.pageType && <Text slot={DETAILS_TOOLBAR_SLOTS.PAGE_TYPE_TEXT}>{staticData.pageType}</Text>}
                  </View>
                </CatchBoundary>
                <View style={actionsView.base}>
                  <CatchBoundary errorComponent={() => null} getResetKey={() => 'reset'} onCatch={(error, errorInfo) => console.error(error, errorInfo)}>
                    {staticData?.slots?.statusComponent && <staticData.slots.statusComponent />}
                    {staticData?.slots?.actionsComponent && <staticData.slots.actionsComponent />}
                  </CatchBoundary>
                </View>
              </View>
            </View>
          </DetailsToolbar>}

        <View style={isCentered ? centeredContentStyles.base : fullWidthContentStyles.base}>
          <CatchBoundary
            errorComponent={ErrorState}
            getResetKey={() => 'reset'}>
            <Outlet />
          </CatchBoundary>
        </View>
      </View>
    </Provider>
  );
}

const centeredStyles = stylex.create({
  base: {
    display: {
      default: 'grid',
      [breakpoints.small]: 'block',
    },
    gridTemplateColumns: {
      default: '1fr minmax(max-content, 592px) 1fr',
      [breakpoints.small]: null,
    },
    gridTemplateRows: '56px 1fr',
    height: '100%',
    overflow: 'auto',
    overflowX: {
      default: null,
      [breakpoints.small]: 'hidden',
    },
    width: '100%',
  },
});

const centeredToolbarStyles = stylex.create({
  base: {
    alignItems: 'center',
    animationFillMode: 'both',
    animationName: scrollBorderAnimation,
    animationTimeline: 'scroll(nearest block)',
    backgroundColor: $.surfaceBGPrimary,
    borderBottomColor: 'transparent',
    borderBottomStyle: 'solid',
    borderBottomWidth: 1,
    display: {
      default: 'grid',
      [breakpoints.small]: 'flex',
    },
    gridColumn: {
      default: '1 / -1',
      [breakpoints.small]: null,
    },
    gridTemplateColumns: {
      default: 'inherit',
      [breakpoints.small]: null,
    },
    paddingBottom: 12,
    paddingLeft: {
      default: null,
      [breakpoints.large]: 12,
    },
    paddingRight: {
      default: null,
      [breakpoints.large]: 16,
    },
    paddingTop: {
      default: null,
      [breakpoints.large]: 12,
    },
    position: 'sticky',
    top: 0,
    zIndex: 10,
  },
  heading: {
    gridColumn: {
      default: '2',
      [breakpoints.small]: '1',
    },
    paddingLeft: 20,
  },
  goBackButton: {
    gridColumn: {
      default: '1',
      [breakpoints.small]: null,
    },
    justifySelf: 'end',
    marginLeft: 20,
    marginRight: -12,
  },
  actions: {
    display: 'flex',
    gap: 8,
    gridColumn: {
      default: '3',
      [breakpoints.small]: null,
    },
    justifySelf: 'end',
    marginLeft: 'auto',
    marginRight: 20,
  },
});

const centeredContentStyles = stylex.create({
  base: {
    gridColumn: {
      default: '2',
      [breakpoints.small]: '1',
    },
    overflow: {
      default: null,
      [breakpoints.small]: 'hidden',
    },
    paddingBottom: 20,
    paddingInline: 20,
    paddingTop: 32,
    width: {
      default: null,
      [breakpoints.small]: '100%',
    },
  },
});

const fullWidthStyles = stylex.create({
  base: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'auto',
    overflowX: {
      default: null,
      [breakpoints.small]: 'hidden',
    },
    width: '100%',
  },
  highlighted: {
    borderColor: $.accentChilli60,
    borderRadius: 4,
    borderStyle: 'solid',
    borderWidth: 1,
  },
});

const actionsView = stylex.create({
  base: {
    marginLeft: 'auto',
  },
});

const fullWidthContentStyles = stylex.create({
  base: {
    alignSelf: 'center',
    overflow: {
      default: null,
      [breakpoints.small]: 'hidden',
    },
    paddingBottom: 120,
    paddingInline: 20,
    paddingTop: 32,
    width: {
      default: 'min(592px, 100%)',
      [breakpoints.small]: '100%',
    },
  },
});
